// Dialog for managing a dog's custom activity list. Reused by:
//   • Weekly Planner  — inline "Manage activities" button
//   • Reminders page  — inline "Manage activities" button
//   • Settings        — per-dog manage button
//
// Backend: /api/dogs/{dog_id}/custom-activities (GET/POST/DELETE).
// Emits `onChanged()` after every mutation so parent pages can re-fetch the
// dog's list and refresh their dropdowns.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ACTIVITIES } from "@/lib/activities";
import { Plus, Trash2, Scissors, Dumbbell, Stethoscope, Tag, GripVertical, Star, X } from "lucide-react";

const CATEGORIES = [
  { value: "training", label: "Training", Icon: Dumbbell, color: "hsl(36 25% 55%)" },
  { value: "grooming", label: "Grooming", Icon: Scissors, color: "hsl(120 13% 20%)" },
  { value: "vet", label: "Vet", Icon: Stethoscope, color: "hsl(17 41% 44%)" },
  { value: "other", label: "Other", Icon: Tag, color: "hsl(197 37% 24%)" },
];

export default function CustomActivitiesDialog({ open, onOpenChange, dogId, dogName, onChanged }) {
  const [items, setItems] = useState([]);
  const [pinned, setPinned] = useState([]);          // [label, ...] in pin order
  const [pinToAdd, setPinToAdd] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ label: "", category: "training" });

  // Drag state: `dragId` is the row currently being dragged, `overId` is the row
  // it's hovering over. We reorder optimistically on drop and PUT the new order.
  const dragId = useRef(null);
  const [overId, setOverId] = useState(null);

  const load = useCallback(async () => {
    if (!dogId) return;
    setLoading(true);
    try {
      const [customRes, pinnedRes] = await Promise.all([
        api.get(`/dogs/${dogId}/custom-activities`),
        api.get(`/dogs/${dogId}/pinned-activities`),
      ]);
      setItems(customRes.data.items || []);
      setPinned((pinnedRes.data.items || []).map((p) => p.label));
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not load activities");
    } finally {
      setLoading(false);
    }
  }, [dogId]);

  useEffect(() => {
    if (open && dogId) load();
    if (!open) setForm({ label: "", category: "training" });
  }, [open, dogId, load]);

  const submit = async (e) => {
    e.preventDefault();
    const label = form.label.trim();
    if (!label) return;
    setSaving(true);
    try {
      await api.post(`/dogs/${dogId}/custom-activities`, { label, category: form.category });
      setForm({ label: "", category: form.category });
      await load();
      onChanged && onChanged();
      toast.success("Activity added");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not add");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/dogs/${dogId}/custom-activities/${id}`);
      await load();
      onChanged && onChanged();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not delete");
    }
  };

  // Pin/unpin built-in activities. Pinned labels are excluded from the picker
  // to avoid double-pin attempts.
  const availableToPin = useMemo(() => {
    const pinnedSet = new Set(pinned);
    return ACTIVITIES.filter((a) => !pinnedSet.has(a.label));
  }, [pinned]);

  const pinActivity = async () => {
    if (!pinToAdd) return;
    try {
      await api.post(`/dogs/${dogId}/pinned-activities`, { label: pinToAdd });
      setPinToAdd("");
      await load();
      onChanged && onChanged();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not pin");
    }
  };

  const unpinActivity = async (label) => {
    try {
      await api.delete(`/dogs/${dogId}/pinned-activities`, { params: { label } });
      await load();
      onChanged && onChanged();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not unpin");
    }
  };

  // Drag-to-reorder handlers. We keep the reorder optimistic (immediate visual
  // feedback) and only call the API on drop.
  const onDragStart = (id) => (e) => {
    dragId.current = id;
    e.dataTransfer.effectAllowed = "move";
    // Firefox needs data set on the transfer to fire drop events reliably.
    e.dataTransfer.setData("text/plain", id);
  };
  const onDragOver = (id) => (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (id !== overId) setOverId(id);
  };
  const onDragLeave = () => setOverId(null);
  const onDrop = (targetId) => async (e) => {
    e.preventDefault();
    const sourceId = dragId.current;
    dragId.current = null;
    setOverId(null);
    if (!sourceId || sourceId === targetId) return;
    const fromIdx = items.findIndex((x) => x.id === sourceId);
    const toIdx = items.findIndex((x) => x.id === targetId);
    if (fromIdx < 0 || toIdx < 0) return;
    const next = items.slice();
    const [moved] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, moved);
    setItems(next);
    try {
      await api.put(`/dogs/${dogId}/custom-activities/reorder`, {
        ids: next.map((x) => x.id),
      });
      onChanged && onChanged();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not save order");
      // Revert on error
      await load();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="custom-activities-dialog">
        <DialogHeader>
          <DialogTitle>Custom activities{dogName ? ` · ${dogName}` : ""}</DialogTitle>
          <DialogDescription>
            Add the routines your kennel uses. They&apos;ll appear in every activity dropdown
            for this dog — Weekly Planner, event logging, and Reminders.
          </DialogDescription>
        </DialogHeader>

        {/* Pinned built-in favourites — float to the top of every dropdown. */}
        <div className="space-y-2" data-testid="pinned-favourites-section">
          <div className="flex items-center gap-2">
            <Star size={14} className="text-primary" />
            <p className="text-xs uppercase tracking-wider text-muted-foreground">
              Pinned favourites ({pinned.length})
            </p>
          </div>
          <div className="flex gap-2">
            <select
              value={pinToAdd}
              onChange={(e) => setPinToAdd(e.target.value)}
              className="flex-1 border border-input rounded-md bg-card px-3 py-2 text-sm"
              data-testid="pin-favourite-select"
            >
              <option value="">Star a built-in activity…</option>
              {availableToPin.map((a) => (
                <option key={a.label} value={a.label}>{a.label}</option>
              ))}
            </select>
            <Button
              type="button"
              onClick={pinActivity}
              disabled={!pinToAdd}
              variant="outline"
              size="sm"
              data-testid="pin-favourite-submit"
            >
              <Star size={13} className="mr-1" /> Pin
            </Button>
          </div>
          {pinned.length > 0 ? (
            <ul className="flex flex-wrap gap-1.5" data-testid="pinned-list">
              {pinned.map((label) => (
                <li
                  key={label}
                  className="inline-flex items-center gap-1 text-xs bg-primary/10 text-primary rounded-full pl-2.5 pr-1 py-1"
                  data-testid={`pinned-item-${label}`}
                >
                  <Star size={11} className="fill-primary" />
                  <span className="max-w-[200px] truncate">{label}</span>
                  <button
                    type="button"
                    onClick={() => unpinActivity(label)}
                    className="text-primary hover:text-destructive p-0.5 rounded-full"
                    title="Unpin"
                    aria-label={`Unpin ${label}`}
                  >
                    <X size={12} />
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-xs text-muted-foreground">
              Star built-in activities so they sit at the very top of every dropdown for this dog.
            </p>
          )}
        </div>

        <div className="border-t border-border" />

        <form onSubmit={submit} className="space-y-3" data-testid="custom-activity-form">
          <div>
            <Label htmlFor="ca-label">Activity name</Label>
            <Input
              id="ca-label"
              value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
              placeholder="e.g. Show ring practice"
              maxLength={60}
              className="mt-1.5"
              data-testid="custom-activity-label"
              autoFocus
            />
          </div>
          <div>
            <Label htmlFor="ca-category">Category</Label>
            <select
              id="ca-category"
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              className="mt-1.5 w-full border border-input rounded-md bg-card px-3 py-2 text-sm"
              data-testid="custom-activity-category"
            >
              {CATEGORIES.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
            <p className="text-[11px] text-muted-foreground mt-1">
              Sets the icon and colour used when this activity shows up on your calendar.
            </p>
          </div>
          <Button
            type="submit"
            disabled={saving || !form.label.trim()}
            className="bg-primary text-primary-foreground hover:bg-primary/90"
            data-testid="custom-activity-submit"
          >
            <Plus size={14} className="mr-1" /> {saving ? "Adding…" : "Add activity"}
          </Button>
        </form>

        <div className="border-t border-border pt-4">
          <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">
            Your activities ({items.length})
            {items.length > 1 ? (
              <span className="normal-case tracking-normal text-muted-foreground/80"> — drag rows to reorder</span>
            ) : null}
          </p>
          {loading ? (
            <p className="text-sm text-muted-foreground py-4">Loading…</p>
          ) : items.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4" data-testid="custom-activities-empty">
              None yet. Add your first routine above.
            </p>
          ) : (
            <ul className="space-y-1.5 max-h-64 overflow-y-auto pr-1" data-testid="custom-activities-list">
              {items.map((it) => {
                const cat = CATEGORIES.find((c) => c.value === it.category) || CATEGORIES[3];
                const Icon = cat.Icon;
                const isOver = overId === it.id;
                return (
                  <li
                    key={it.id}
                    draggable
                    onDragStart={onDragStart(it.id)}
                    onDragOver={onDragOver(it.id)}
                    onDragLeave={onDragLeave}
                    onDrop={onDrop(it.id)}
                    className={`flex items-center gap-2 text-sm bg-secondary/50 rounded-md px-2 py-2 cursor-grab active:cursor-grabbing transition-all ${
                      isOver ? "ring-2 ring-primary/50 bg-secondary" : ""
                    }`}
                    data-testid={`custom-activity-item-${it.id}`}
                  >
                    <GripVertical size={14} className="text-muted-foreground shrink-0" aria-hidden />
                    <Icon size={14} style={{ color: cat.color }} className="shrink-0" />
                    <span className="flex-1 truncate">{it.label}</span>
                    <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                      {cat.label}
                    </span>
                    <button
                      type="button"
                      onClick={() => remove(it.id)}
                      className="text-muted-foreground hover:text-destructive p-1"
                      title="Remove"
                      data-testid={`custom-activity-remove-${it.id}`}
                    >
                      <Trash2 size={13} />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
